import FileData from './FileData';
import {randomUint8Array} from '../arrayUtils';

/**
 * Extracts a preview image from a video file at the specified time.
 * 
 * @param videoFile - The video file to extract the preview from.
 * @param timeInSeconds - The time in seconds at which to extract the frame (default: 0).
 * @param maxWidth - Maximum width of the resulting thumbnail (keeps aspect ratio, default: 320).
 * @param quality - JPEG quality (0-1, default: 0.8).
 * @returns A promise that resolves with a structured FileData.
 */
export default function getVideoPreviewImage(
  videoFile: FileData,
  timeInSeconds: number = 0,
  maxWidth: number = 320,
  quality: number = 1
): Promise<FileData> {
  return new Promise((resolve, reject) => {
    try {
      const video = document.createElement('video');
      video.muted = true;

      video.src = videoFile.objectUrl;

      // Ensure metadata is loaded so we can seek
      video.addEventListener('loadedmetadata', () => {
        // Seek to the desired time
        video.currentTime = Math.min(timeInSeconds, video.duration);

        // Once we've seeked to the desired time, capture the frame
        video.addEventListener('seeked', () => {
          try {
            // Create a canvas element to draw the video frame
            const canvas = document.createElement('canvas');
            const mimeType = 'image/jpeg';

            // Calculate dimensions while maintaining aspect ratio
            let width = video.videoWidth;
            let height = video.videoHeight;

            if (width > maxWidth) {
              const ratio = maxWidth / width;
              width = maxWidth;
              height = Math.floor(height * ratio);
            }

            canvas.width = width;
            canvas.height = height;

            // Draw the video frame to the canvas
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              throw new Error('Could not get canvas context');
            }

            // Draw the video frame to the canvas
            ctx.drawImage(video, 0, 0, width, height);

            // Convert canvas to blob
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to create blob from canvas'));
                  return;
                }

                // Create a file from the blob
                const fileName = `${videoFile.fileName.split('.')[0]}_preview.jpg`;
                const previewFile = new File([blob], fileName, {type: mimeType});

                // Return the structured result
                resolve({
                  fileId: randomUint8Array(),
                  file: previewFile,
                  fileName,
                  objectUrl: URL.createObjectURL(blob),
                  width,
                  height,
                  mimeType
                });
              },
              mimeType,
              quality
            );
          } catch (error) {
            reject(error);
          }
        });
      });

      // Handle errors
      video.addEventListener('error', (e) => {
        reject(new Error(`Video loading error: ${e.message}`));
      });

      // Some browsers need this
      video.load();
    } catch (error) {
      reject(error);
    }
  });
}
